/**
 * <ColorReassignNotice> + helpers, T17 (2a).
 *
 * The SERVER is the uniqueness arbiter for player colors. When a joining
 * player's picked color is already taken, the server reassigns them a random
 * unused color and sends the ASSIGNED color back on the wire (in the
 * serialized players list of ROOM_STATE_SYNC). The client compares its PICKED
 * color to its ASSIGNED color and, when they differ, surfaces a brief notice
 * so the player's pick is never SILENTLY overridden.
 *
 * No protocol bump: the assigned color already travels in SerializedPlayer.
 */
import { useEffect, useState } from "preact/hooks";
import { BRAND_COLORS } from "@drawlol/shared";

/** The reassignment notice auto-dismisses this long after it appears (item 8). */
export const COLOR_REASSIGN_NOTICE_DURATION_MS = 5000;

/**
 * Human-readable name for each brand hex. Mirrors the inline comments on the
 * `BrandColor` union in shared/src/types.ts. Built from BRAND_COLORS so it
 * can't silently fall out of sync with the canonical palette length.
 */
const NAME_BY_HEX: Record<string, string> = {
  "#000000": "black",
  "#e63946": "red",
  "#ffd23f": "yellow",
  "#4361ee": "blue",
  "#7cb518": "green",
  "#8b5a2b": "brown",
  "#f77f00": "orange",
  "#9d4edd": "purple",
};

/** The 8 canonical brand hexes → human names. */
export const BRAND_COLOR_NAMES: Record<string, string> = Object.fromEntries(
  BRAND_COLORS.map((hex) => [hex, NAME_BY_HEX[hex] ?? hex]),
);

/** Human name for a hex, falling back to the hex itself when unknown. */
export function colorName(hex: string): string {
  return BRAND_COLOR_NAMES[hex] ?? hex;
}

/**
 * The reassignment notice message, or null when there's nothing to surface:
 *  - assigned is empty/undefined (not yet known) → null
 *  - assigned === picked (server honored the pick) → null
 *  - otherwise → "Color taken: you're {assignedName}."
 */
export function colorReassignNotice(
  pickedColor: string,
  assignedColor: string | undefined,
): string | null {
  if (!assignedColor) return null;
  if (assignedColor === pickedColor) return null;
  return `Color taken — you're ${colorName(assignedColor)}.`;
}

export type ColorReassignNoticeProps = {
  pickedColor: string;
  assignedColor: string | undefined;
};

export function ColorReassignNotice({ pickedColor, assignedColor }: ColorReassignNoticeProps) {
  const message = colorReassignNotice(pickedColor, assignedColor);
  // item 8, the "Color taken, you're {color}." notice used to stay on screen
  // forever. Auto-hide it 5s after it appears. The effect re-arms whenever the
  // message changes (a fresh reassignment shows it again) and clears its timer on
  // unmount / message change so a pending timeout never fires against a stale notice.
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (!message) return;
    setDismissed(false);
    const timer = setTimeout(() => setDismissed(true), COLOR_REASSIGN_NOTICE_DURATION_MS);
    return () => clearTimeout(timer);
  }, [message]);

  if (!message || dismissed) return null;
  return (
    <p class="join-color-reassigned" role="status" aria-live="polite">
      {message}
    </p>
  );
}
